import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

// Auto-logout after 10 minutes (600,000 ms) of inactivity
const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000;
const LAST_ACTIVITY_KEY = 'printspace_last_activity';

// Events that count as user activity
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export const AuthProvider = ({ children }) => {
  // Normalise allowedPages — MySQL JSON columns can come back as strings
  const normaliseUser = (u) => {
    if (!u) return u;
    if (typeof u.allowedPages === 'string') {
      try { u.allowedPages = JSON.parse(u.allowedPages); } catch { u.allowedPages = []; }
    }
    if (u.allowedPages !== null && !Array.isArray(u.allowedPages)) u.allowedPages = [];
    return u;
  };

  const [user, setUser] = useState(() => {
    try {
      const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
      const expired = lastActivity && (Date.now() - lastActivity > INACTIVITY_TIMEOUT_MS);
      if (expired) {
        localStorage.removeItem('printspace_token');
        localStorage.removeItem('printspace_user');
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        return null;
      }
      return JSON.parse(localStorage.getItem('printspace_user'));
    } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  // Ref so the timer callback always sees the latest logout function without re-registering listeners
  const inactivityTimerRef = useRef(null);
  const logoutRef = useRef(null);

  const logout = useCallback((silent = false) => {
    localStorage.removeItem('printspace_token');
    localStorage.removeItem('printspace_user');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setUser(null);
    if (silent) {
      toast('You were logged out due to inactivity.', { icon: '⏱️' });
    } else {
      toast.success('Logged out successfully.');
    }
  }, []);

  // Keep logoutRef current so the timer callback doesn't stale-close over an old logout
  logoutRef.current = logout;

  // ── Inactivity timer ──────────────────────────────────────────────────────
  const isLoggedIn = !!user;

  const resetTimer = useCallback(() => {
    // Stamp current time so tab-close + reopen can check elapsed time
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      logoutRef.current(true);
    }, INACTIVITY_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    // Only run when logged in. Use boolean so user object churn doesn't re-register listeners.
    if (!isLoggedIn) {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      return;
    }

    // Stamp activity now (covers fresh login and tab reopen)
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

    // Start the countdown
    resetTimer();

    // Reset on any activity
    ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, resetTimer, { passive: true }));

    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [isLoggedIn, resetTimer]);
  // ─────────────────────────────────────────────────────────────────────────

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await API.post('/auth/login', { email, password });
      localStorage.setItem('printspace_token', data.token);
      localStorage.setItem('printspace_user', JSON.stringify(data.user));
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      setUser(normaliseUser(data.user));
      toast.success(`Welcome back, ${data.user.name}!`);
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, email, password, phone) => {
    setLoading(true);
    try {
      const { data } = await API.post('/auth/register', { name, email, password, phone });
      localStorage.setItem('printspace_token', data.token);
      localStorage.setItem('printspace_user', JSON.stringify(data.user));
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      setUser(normaliseUser(data.user));
      toast.success('Account created successfully!');
      return data.user;
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async (credentialResponse) => {
    setLoading(true);
    try {
      const { data } = await API.post('/auth/google', { token: credentialResponse.credential });
      localStorage.setItem('printspace_token', data.token);
      localStorage.setItem('printspace_user', JSON.stringify(data.user));
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      setUser(normaliseUser(data.user));
      toast.success(`Welcome, ${data.user.name}!`);
      return { user: data.user, isNew: data.isNew };
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (updatedUser) => {
    const merged = { ...user, ...updatedUser };
    localStorage.setItem('printspace_user', JSON.stringify(merged));
    setUser(normaliseUser(merged));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout: () => logout(false), updateUser, googleLogin, isAdmin: user?.role === 'admin',
      isStaff: user?.role === 'staff' }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);